import React, { useEffect, useMemo, useRef, useState } from "react";
import FullModal from "../FullModal";
import { getImageBaseUrl } from "../../services/s3-utils";
import QalortButton from "../Button";
import badge from "../../assets/badge.svg";
import info from "../../assets/info.svg";
import tickIcon from "../../assets/tick-confetti.svg";
import infoRed from "../../assets/info-red.svg";
import { Link, useNavigate } from "react-router-dom";
import FaqAccordion from "../FaqAccordion";
import { useUpcomingMeetings } from "../../hooks/useUpcomingMeetings";
import { LoadingOutlined } from "@ant-design/icons";
import {
  calculateTimeDifference,
  convertTz,
  getTimeToMeeting,
} from "../../utils/timeHelpers";
import dayjs from "dayjs";
import { GetCohortDetails, PayForCohort } from "../../services/cohortService";
import { useAppointments } from "../../hooks/useAppointments";
import { Modal, notification } from "antd";
import { useSelector } from "react-redux";
import Payment from "../../pages/Payment";
import ShareUrl from "../ShareUrl";
import useEnableJoinBtn from "../../hooks/useEnableJoinBtn";
import JoinMeetinButton from "../JoinMeetinButton";
import { useDailyEvents } from "../../services/daily-utils/useDailyEvents";
import { useCohorts } from "../../hooks/useCohorts";
import Empty from "../Empty";
import ReactPlayer from "react-player";
import { formatCurrency } from "../../services/Utils";
import useInterval from "../../hooks/useInterval";
import CountdownTimer from "../CountdownTimer";

function CohortDetailsPreview({ isOpen, onClose, slug }) {
  const initialized = useRef(false);
  const { allUpcomingMeetingsByDate } = useUpcomingMeetings(slug);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const qalortUser = useSelector((state) => state.user.userData);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const navigation = useNavigate();
  const [fullCohortDetails, setCohortDetails] = useState(null);
  const navigate = useNavigate();
  const [successModal, setSuccessModal] = useState(false);
  const { data: MyCohorts } = useCohorts(qalortUser?.username);
  const {
    data: myAppointments,
    mutation: mutationHandler,
    refetch: getMyAppointments,
  } = useAppointments("cohorts");
  const { disableJoinBtn } = useEnableJoinBtn(fullCohortDetails);
  const [modalVisible, setModalVisible] = useState(false);
  const [cart, setCart] = useState(null);
  const { startHairCheck } = useDailyEvents();

  const isExpired = useMemo(() => {
    return dayjs(fullCohortDetails?.end_date).isSame(
      dayjs().format("YYYY-MM-DD")
    )
      ? false
      : dayjs(fullCohortDetails?.end_date).isBefore(dayjs());
  }, [fullCohortDetails?.end_date]);

  const isJoined = useMemo(() => {
    return (
      fullCohortDetails?.user_id === qalortUser?.id ||
      !!myAppointments?.find((a) => a.slug === fullCohortDetails?.slug)
    );
  }, [fullCohortDetails, myAppointments?.length]);

  const fetchCohortInfo = async () => {
    setLoading(true);
    try {
      const res = await GetCohortDetails(slug);
      const defaultCohortData = res.data.data;

      let offSet = defaultCohortData.timezone || null;
      const timezoneAdjustedCohort = {
        ...res.data.data,
        schedules: defaultCohortData.schedules.map((schedule) => {
          return {
            ...schedule,
            start_time: convertTz(schedule.start_time, offSet),
            end_time: convertTz(schedule.end_time, offSet),
          };
        }),
      };

      setCohortDetails(timezoneAdjustedCohort);
    } catch (error) {
      notification.error({
        message: error.response?.data?.message || "An error occured",
      });
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const joinCohortHandler = async () => {
    let data = {
      cohort_id: fullCohortDetails?.id,
      item_type: "cohort",
    };
    setJoining(true);
    try {
      const res = await mutationHandler.mutateAsync(data); //JoinCohort(data);
      let cart_ = res.data.data;
      setCart(cart_);
      setModalVisible(true);
      getMyAppointments();
    } catch (error) {
      notification.error({
        description:
          error?.response?.data?.message ||
          "An error occured, Please try again",
      });
    } finally {
      setJoining(false);
    }
  };

  const proceedToJoin = () => {
    if (!qalortUser) {
      const returnUrl = `${window.location.pathname}${window.location.search}`;
      navigation(`/login?returnUrl=${returnUrl}`);
    } else {
      fullCohortDetails?.price && fullCohortDetails?.price > 0
        ? joinCohortHandler()
        : makePayment(null, null);
    }
  };

  const makePayment = async (cart_reference, cart_, payment_option) => {
    setJoining(true);
    let data = {
      cohort_id: fullCohortDetails?.id,
      payment_option,
    };
    if (cart_reference) {
      data.cart_id = cart_.id;
      data.cart_reference = cart_reference.reference;
    }
    PayForCohort(data)
      .then((res) => {
        // Refresh the appointments list to include the newly purchased cohort
        getMyAppointments();

        // Show the success modal
        setSuccessModal(true);

        // Log success for debugging
        console.log("Cohort purchase successful:", fullCohortDetails?.slug);
      })
      .catch((error) => {
        notification.error({
          description:
            error?.response?.data?.message ||
            "An error occurred, Please try again",
        });
        console.error("Cohort purchase error:", error);
      })
      .finally(() => setJoining(false));
  };

  const thisCohortData = useMemo(() => {
    return MyCohorts?.find((c) => c.slug === slug);
  }, [MyCohorts, slug]);

  useEffect(() => {
    isOpen && !!slug && fetchCohortInfo();
  }, [isOpen, slug]);

  useEffect(() => {
    completePayment();
  }, [window.location.search, fullCohortDetails]);

  const tick = useInterval(() => {}, 1000);
  const durationToMeeting = useMemo(() => {
    if (!allUpcomingMeetingsByDate?.length) {
      return null;
    }
    return getTimeToMeeting(allUpcomingMeetingsByDate[0]);
  }, [allUpcomingMeetingsByDate, tick]);

  const completePayment = () => {
    let params = new URLSearchParams(window.location.search);
    const cart_id = params.get("cart_id");
    const cart_reference = params.get("cart_reference");
    const status = params.get("status");

    // Check if this is a successful payment return from Stripe
    if (
      status === "success" &&
      fullCohortDetails?.id &&
      cart_id &&
      cart_reference
    ) {
      console.log("Processing successful payment return:", {
        cart_id,
        cart_reference,
      });
      makePayment({ reference: cart_reference }, { id: cart_id }, "stripe");

      // Clean up the URL to remove the query parameters
      if (window.history && window.history.replaceState) {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  };

  useEffect(() => {
    if (!!thisCohortData && !disableJoinBtn && isJoined) {
      startHairCheck(
        thisCohortData?.meeting_link,
        thisCohortData?.token,
        thisCohortData
      );
    }
  }, [disableJoinBtn, isJoined, thisCohortData]);

  return (
    <>
      <FullModal
        isOpen={isOpen}
        onClose={onClose}
        header={
          <Link to="/">
            <img
              src={`${getImageBaseUrl}/assets/logo.svg`}
              className="h-8 w-[103px]"
              alt=""
            />
          </Link>
        }
        extra={
          <div className="hidden md:flex gap-5">
            <p className="mb-0"></p>
            <a href="#instructor" className="text-main p-sm">
              Instructor
            </a>
            <p className="mb-0">
              <a href="#curriculum" className="text-main p-sm">
                Curriculum
              </a>
            </p>
            {fullCohortDetails?.faqs?.length && (
              <p className="mb-0">
                <a href="#faq" className="text-main p-sm">
                  FAQ’s
                </a>
              </p>
            )}
          </div>
        }
      >
        {loading ? (
          <div
            className="flex flex-col items-center justify-center h-screen w-full z-50"
            data-testid="app_loader"
          >
            <LoadingOutlined style={{ fontSize: "50px" }} />
          </div>
        ) : fullCohortDetails === null ? (
          <Empty title={"Oops! Cohort not found"} />
        ) : (
          <div className="max-w-[1093px] mx-auto">
            <div className="relative overflow-hidden rounded-[10px] border border-grey-2 shadow-[0_4px_30px_0_#0606110F] mt-0 md:mt-[30px] h-[250px] [@media(max-width:1023px)]:-mx-5">
              <img src={fullCohortDetails?.banner} alt="" className="w-full" />
              <div className="absolute left-5 bottom-5 bg-white p-10 rounded-xl border border-[#E8E8EA] hidden lg:block w-full md:w-[60%]">
                <p className="text-[20px] leading-[33px] text-main font-bold mb-[3px] line-clamp-2">
                  {fullCohortDetails?.title}
                </p>
                <p className="p-sm mb-0 font-medium text-main line-clamp-2">
                  {fullCohortDetails?.description}
                </p>
              </div>
            </div>
            <div className="mt-10 flex justify-between [@media(max-width:1023px)]:flex-col-reverse gap-[33px]">
              <div className="flex flex-col gap-[33px] md:border border-[#DCDCE9] flex-1 rounded-[12px] p-5 mb-[400px] md:mb-0">
                <div>
                  <p className="p-xs font-bold text-tint mb-0" id="instructor">
                    Hosted by
                  </p>
                  <button
                    className="w-fit"
                    onClick={() => {
                      navigate(
                        `/mentor/${fullCohortDetails?.main_organizer?.username}`
                      );
                    }}
                  >
                    <div className="flex gap-[15px] mt-[9px] items-center w-fit">
                      <div
                        className="h-[72px] w-[72px] rounded-full bg-[#ACADED] flex items-center justify-center"
                        style={{
                          backgroundImage: `url(${fullCohortDetails?.main_organizer?.profile})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      ></div>
                      <p className="p-sm font-semibold text-main mb-0 capitalize">
                        {fullCohortDetails?.main_organizer?.name}
                      </p>
                    </div>
                  </button>
                </div>

                {fullCohortDetails?.intro_video && (
                  <div>
                    <h4 className="text-main font-bold mb-3.5">
                      Introduction Video
                    </h4>
                    <ReactPlayer
                      url={fullCohortDetails?.intro_video}
                      className="!w-[400px] !h-[230px] rounded-md overflow-hidden"
                    />
                  </div>
                )}

                {fullCohortDetails?.long_description && (
                  <div id="curriculum">
                    <h4 className="text-main font-bold mb-[3px]">
                      What you will learn.
                    </h4>
                    <p
                      className="p-xs font-medium text-main mb-0 inner-html"
                      dangerouslySetInnerHTML={{
                        __html: fullCohortDetails?.long_description,
                      }}
                    ></p>
                  </div>
                )}

                {fullCohortDetails?.topics?.length > 0 && (
                  <div>
                    <div className="mt-3.5 flex flex-col gap-7">
                      <div>
                        <h4 className="text-main font-bold mb-[3px]">
                          Topics we will cover
                        </h4>
                        <ul className="pl-6">
                          {fullCohortDetails?.topics?.map((topic, i) => (
                            <li
                              className="p-sm mb-0 mt-2.5 font-medium text-main list-disc"
                              key={i}
                            >
                              {topic.title}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {fullCohortDetails?.audiences?.length > 0 && (
                  <div>
                    <h4 className="text-main font-bold mb-[3px]">
                      Who is this cohort for?
                    </h4>
                    <div className="p-sm font-medium text-main mb-0 flex flex-wrap gap-2">
                      {fullCohortDetails.audiences.map((audience, i) => (
                        <div
                          key={i}
                          className="bg-[#DFDFFE] px-4 py-2 rounded-[10px] capitalize"
                        >
                          {audience.audience}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-main font-bold mb-[3px]">
                    Your instructor
                  </h4>
                  <div className="p-sm font-medium text-main mb-0 flex flex-wrap gap-2">
                    <ul className="pl-6 list-disc">
                      <li className="capitalize">
                        {fullCohortDetails?.main_organizer?.name}
                      </li>
                    </ul>
                  </div>
                </div>

                {fullCohortDetails?.requirements?.length > 0 && (
                  <div>
                    <h4 className="text-main font-bold mb-[3px]">
                      What you need for this Cohort:
                    </h4>
                    <div className="p-[4px] font-medium text-main mb-0 gap-2">
                      {fullCohortDetails?.requirements?.map(
                        (requirement, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 mb-2 p-xs font-medium text-[#535393]"
                          >
                            <span>
                              <img src={badge} alt="" />
                            </span>
                            {requirement.content}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-main font-bold mb-[3px]">Schedule</h4>
                  <div className="p-sm font-medium text-main mb-0 flex flex-wrap gap-2">
                    <div className="flex flex-col mb-[25px]">
                      <p className="p-xs flex font-medium text-main mb-0">
                        <div className="w-[180px] mb-2">Frequency</div>
                        <span className="text-[#4F4FD0] capitalize">
                          {fullCohortDetails?.frequency}
                        </span>
                      </p>
                      <p className="p-xs flex font-medium text-main mb-0">
                        <div className="w-[180px] mb-2">Start Date</div>
                        <span className="text-[#4F4FD0]">
                          {dayjs(fullCohortDetails?.start_date).format(
                            "MMM DD, YYYY"
                          )}
                        </span>
                      </p>
                      <p className="p-xs flex font-medium text-main mb-0">
                        <div className="w-[180px] mb-2">End Date</div>
                        <span className="text-[#4F4FD0]">
                          {dayjs(fullCohortDetails?.end_date).format(
                            "MMM DD, YYYY"
                          )}

                          {isExpired && (
                            <span className="red"> (Cohort has Expired)</span>
                          )}
                        </span>
                      </p>
                      <p className="p-xs flex font-medium text-main mb-0">
                        <div className="w-[180px] mb-2">Time</div>
                        <span className="text-[#4F4FD0]">
                          {dayjs(
                            fullCohortDetails?.schedules?.[0].start_time,
                            "HH:mm"
                          ).format("h:mma")}{" "}
                          -{" "}
                          {dayjs(
                            fullCohortDetails?.schedules?.[0].end_time,
                            "HH:mm"
                          ).format("h:mma")}
                        </span>
                      </p>
                      <p className="p-xs flex font-medium text-main mb-0">
                        <div className="w-[180px] mb-2">Duration</div>
                        <span className="text-[#4F4FD0]">
                          {calculateTimeDifference(
                            fullCohortDetails?.schedules?.[0].end_time,
                            fullCohortDetails?.schedules?.[0].start_time
                          )}{" "}
                          minutes
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {fullCohortDetails?.faqs?.filter((f) => f.question && f.answer)
                  .length > 0 && (
                  <div className="w-full" id="faq">
                    <h4 className="text-main font-bold mb-3.5">
                      Frequently asked questions
                    </h4>
                    <div className="flex flex-col gap-4">
                      {fullCohortDetails?.faqs?.map((faq, i) => (
                        <FaqAccordion
                          question={faq.question}
                          answer={faq.answer}
                          key={i}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="md:sticky fixed top-unset bottom-1 md:top-[100px] md:bottom-unset right-0 md:bg-white px-5 pb-6 md:pb-2 pt-7 border-grey-2 md:shadow-[0_4px_30px_0_#0606110F] h-fit rounded-[10px] [@media(max-width:1023px)]:mt-[-193px] z-10 w-[100%] md:ml-0 md:w-[350px]">
                <div className="hidden md:block">
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {fullCohortDetails?.category ? (
                      <div className="rounded-[32px] border border-[#4F4FD0] p-xs font-semibold text-[#4F4FD0] px-2.5 py-[7px] text-center one-liner">
                        {fullCohortDetails?.category}
                      </div>
                    ) : (
                      <div />
                    )}
                    {disableJoinBtn && (
                      <p className="p-xs text-[#FB3E6C] font-semibold mb-0">
                        This Cohort is not live
                      </p>
                    )}
                  </div>
                  <p className="text-[20px] leading-[33px] text-main font-bold mb-1.5 max-w-[272px]">
                    {fullCohortDetails?.title}
                  </p>
                  <Link
                    to={`/mentor/${fullCohortDetails?.main_organizer?.username}`}
                  >
                    <p className="p-sm font-semibold text-tint mb-5">
                      By{" "}
                      <span className="text-[#4F4FD0] capitalize">
                        {fullCohortDetails?.main_organizer?.name}
                      </span>
                    </p>
                  </Link>
                </div>

                <div className="rounded-[10px] p-[17px] bg-white md:bg-[#F7F7FC] border-[#ECEBF5] border md:border-0">
                  {!isExpired && (
                    <div className="hidden md:block">
                      <div className="flex flex-col mb-[25px]">
                        <p className="p-xs font-medium text-main mb-2 flex justify-between items-center">
                          <span className="font-semibold">Price</span>
                          <span className="text-[#4F4FD0]">
                            {fullCohortDetails?.price > 0
                              ? `${
                                  fullCohortDetails?.currency
                                } ${formatCurrency(
                                  fullCohortDetails?.price / 100
                                )}`
                              : "Free"}
                          </span>
                        </p>
                        <p className="p-xs font-medium text-main mb-2 flex justify-between items-center">
                          <span className="font-semibold">Start Date</span>
                          <span className="text-[#4F4FD0]">
                            {dayjs(fullCohortDetails?.start_date).format(
                              "MMM DD, YYYY"
                            )}
                          </span>
                        </p>

                        <p className="p-xs font-medium text-main mb-2 flex justify-between items-center">
                          <span className="font-semibold">End Date</span>
                          <span className="text-[#4F4FD0]">
                            {dayjs(fullCohortDetails?.end_date).format(
                              "MMM DD, YYYY"
                            )}
                          </span>
                        </p>
                        <p className="p-xs font-medium text-main mb-2 flex justify-between items-center">
                          <span className="font-semibold">Time</span>
                          <span className="text-[#4F4FD0]">
                            {dayjs(
                              fullCohortDetails?.schedules?.[0].start_time,
                              "HH:mm"
                            ).format("h:mma")}{" "}
                            -{" "}
                            {dayjs(
                              fullCohortDetails?.schedules?.[0].end_time,
                              "HH:mm"
                            ).format("h:mma")}
                          </span>
                        </p>
                        <p className="p-xs font-medium text-main mb-2 flex justify-between items-center">
                          <span className="font-semibold">
                            Session Duration
                          </span>
                          <span className="text-[#4F4FD0]">
                            {calculateTimeDifference(
                              fullCohortDetails?.schedules?.[0].end_time,
                              fullCohortDetails?.schedules?.[0].start_time
                            )}{" "}
                            minutes
                          </span>
                        </p>
                      </div>
                      {fullCohortDetails?.requirements?.length > 0 && (
                        <div className="mb-[17px]">
                          <p className="font-bold p-xs text-main mb-[5px]">
                            What you need for this cohort:
                          </p>
                          <div className="p-4 rounded-md bg-pri-tint-2 flex flex-col gap-1">
                            {fullCohortDetails?.requirements?.map(
                              (requirement, i) => (
                                <p
                                  key={i}
                                  className="flex gap-2.5 items-center p-xs font-medium text-tint mb-0"
                                >
                                  <span>
                                    <img src={badge} alt="" />
                                  </span>
                                  {requirement.content}
                                </p>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col gap-2.5 bg-light-tint md:bg-transparent p-4 md:p-0 rounded-[12px]">
                    {isExpired ? (
                      <div className="bg-white flex items-center border border-dashed border-[#E8E8F1] p-3 text-[#535393] text-[13px] rounded-[10px]">
                        <img src={infoRed} className="mr-2" alt="" />
                        <p className="m-0">This Cohort has Expired</p>
                      </div>
                    ) : !isJoined ? (
                      <QalortButton
                        disabled={isJoined}
                        type="primary"
                        className="py-3 px-9 w-full"
                        onClick={() => {
                          proceedToJoin();
                        }}
                        loading={joining}
                      >
                        Join Cohort
                      </QalortButton>
                    ) : (
                      <div className="bg-white flex items-center border border-dashed border-[#E8E8F1] p-3 text-[#535393] text-[13px] rounded-[10px]">
                        <img src={info} className="mr-2" alt="" />
                        <p className="m-0">
                          {fullCohortDetails?.user_id === qalortUser?.id
                            ? "You created this Cohort."
                            : "You have already joined this Cohort."}
                        </p>
                      </div>
                    )}
                    {!disableJoinBtn && isJoined && (
                      <JoinMeetinButton
                        className={`flex items-center justify-center text-sm py-2 px-4 mr-2 rounded-md border-pri bg-pri text-white`}
                        meetingInfo={thisCohortData}
                      >
                        Join Live Session
                      </JoinMeetinButton>
                    )}

                    {isJoined && durationToMeeting && !isExpired && (
                      <>
                        <p className="text-center text-[#7575A8] mb-0 text-[12px] mt-2">
                          Starts in
                        </p>
                        <div className="flex justify-center p-2 border-[#E8E8F1] border bg-white rounded-[8px]">
                          <CountdownTimer
                            durationToMeeting={durationToMeeting}
                          />
                        </div>
                      </>
                    )}

                    {!isExpired && (
                      <QalortButton
                        className="border border-pri bg-pri-tint-3 pri py-3 px-9 w-full"
                        onClick={() => setShareModalVisible(true)}
                      >
                        Share Cohort
                      </QalortButton>
                    )}
                  </div>
                </div>
                <Link to={"https://qalort.com"} target="_blank">
                  <p className="text-center font-bold text-tint text-[14px] mt-2 mb-0">
                    Powered by Qalort
                  </p>
                </Link>
              </div>
            </div>
          </div>
        )}
      </FullModal>

      <ShareUrl
        title="Cohort"
        visible={shareModalVisible}
        onCancel={() => setShareModalVisible(false)}
        setShareModalVisible={setShareModalVisible}
        url={`${window.location.origin}/ct/${fullCohortDetails?.slug}`}
        cohortName={fullCohortDetails?.title}
      />

      {modalVisible && (
        <Payment
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          cohortDetails={fullCohortDetails}
          itemInfo={{
            amount: fullCohortDetails?.price,
            title: fullCohortDetails?.title,
            currency: fullCohortDetails?.currency,
            banner: fullCohortDetails?.banner,
          }}
          service="cohort"
          onPaymentSuccess={(e) =>
            makePayment({ cart_reference: e }, { ...cart }, "stripe")
          }
          stripe_cancel_url={window.location.href}
          stripe_success_url={`${window.location.origin}${window.location.pathname}?status=success&cart_id=${cart?.id}&cart_reference=${cart?.reference}`}
          cart={cart}
          title={`Join Cohort`}
        />
      )}

      <Modal
        visible={successModal}
        footer={null}
        closable={true}
        onCancel={() => setSuccessModal(false)}
        className="recurring-modal !w-[470px] h-[480px]"
        maskClosable={false} // Prevent closing by clicking outside
      >
        <img src={tickIcon} alt="" className="w-full -mt-[30px]" />
        <div className="flex flex-col justify-center items-center mx-auto -mt-12">
          <h6 className="text-dark mb-0 font-medium mt-6 text-center">
            You have successfully joined this cohort
          </h6>
          <p className="p-xs mt-2 mb-6 text-tint font-medium">
            Click on the button below to view your cohort messages
          </p>

          <QalortButton
            testid="view_cohort"
            className="!py-3 w-full"
            type="primary"
            onClick={() => {
              // Ensure we're navigating to the correct URL format
              const cohortSlug = fullCohortDetails?.slug || "";
              const messagesUrl = `/cohort/messages/${cohortSlug}`;

              console.log("Navigating to cohort messages:", messagesUrl);

              // Close the modal before navigation to prevent any issues
              setSuccessModal(false);

              // Navigate to the cohort messages page
              setTimeout(() => {
                navigation(messagesUrl);
              }, 100);
            }}
          >
            View Cohort
          </QalortButton>
        </div>
      </Modal>
    </>
  );
}

export default CohortDetailsPreview;
